/**
 * Total Knee Arthroplasty (TKA) Impairment Calculator
 * Ported from N8N workflow.
 * Based on AMA Guides 5th Edition, Tables 17-33 & 17-35, p546-549
 * Uses point-based scoring system with deductions
 */

// PORTED VERBATIM from QME - Tool src/lib/ratings/calculators/tka.ts (the trusted
// reference; its scripts/verify-legacy-calcs.mts pins it). Only these local
// types replace the '@/types/ratings' import. Keep in sync with the reference:
// src/utils/tka.test.ts repeats its verify cases.
export interface TkaSideInput {
  pain?: string | null;
  rom_min?: number | null;
  rom_max?: number | null;
  stability_ap?: string | number | null;
  stability_mediolateral?: string | number | null;
  flexion_contracture?: number | null;
  extensor_lag?: number | null;
  alignment?: number | null;
}
export interface CalculatorInput {
  extractedData: { knees?: { tka_right?: TkaSideInput; tka_left?: TkaSideInput } } & Record<string, any>;
  side?: 'right' | 'left';
}
export interface CalculatorOutput {
  narrative: string;
  le?: number;
  wpi: number;
  breakdown?: Record<string, any>;
}

// ============= HELPER FUNCTIONS =============

/**
 * Normalize TKA pain text to canonical bucket
 */
function normPain(pain: string | null | undefined): string | null {
  if (!pain) return null;
  const s = String(pain)
    .toLowerCase()
    .replace(/–|—/g, "-")         // normalize dashes
    .replace(/\s*-\s*/g, " - ")   // space around hyphen
    .replace(/\s+/g, " ")         // collapse spaces
    .trim();

  // Common synonyms → canonical labels
  if (s === "none" || /no pain/.test(s)) return "none";
  if (s === "mild" || /\bmild(?!ly)\b/.test(s)) return "mild";
  if (s.includes("stairs only")) return "stairs only";
  if (s.includes("walking and stairs")) return "walking and stairs";
  if (s === "moderate - occasional" || /moderate.*occasional/.test(s)) return "moderate - occasional";
  if (s === "moderate continual" || s === "moderate - continual" || /moderate.*continual/.test(s)) return "moderate - continual";
  if (s === "severe" || /severe/.test(s)) return "severe";
  return s; // fallback to normalized raw
}

function tkaPainToPoints(pain: string | null | undefined): number | null {
  const p = normPain(pain);
  if (!p) return null;
  if (p === "none") return 50;
  if (p === "mild") return 45;
  if (p === "stairs only") return 40;
  if (p === "walking and stairs") return 30;
  if (p === "moderate - occasional") return 20;
  if (p === "moderate continual" || p === "moderate - continual") return 10;
  if (p === "severe") return 0;
  return null; // Unknown value
}

/**
 * ROM: 1 point per 5° of arc, CAPPED at 25 points.
 * Table 17-35.b (Rating Knee Replacement Results, p549) prints
 * "Range of Motion — Add 1 point per 5° — 25", i.e. 25 is the maximum ROM
 * contribution. Without the cap a large arc (e.g. 130° → 26) over-credits ROM
 * and inflates the total point score.
 */
function tkaRangeOfMotionPoints(rom_min: number | null | undefined, rom_max: number | null | undefined): number | null {
  if (rom_min == null || rom_max == null) return null;
  let min = Number(rom_min), max = Number(rom_max);
  if (Number.isNaN(min) || Number.isNaN(max)) return null;
  if (max < min) [min, max] = [max, min];           // guard inverted inputs
  const arc = Math.max(0, max - min);
  return Math.min(25, Math.floor(arc / 5));
}

/**
 * Parse comparator + number from strings like "<5mm", ">= 6 degrees", "7"
 */
function parseCompNumber(v: any): { comp: string | null; num: number | null } {
  if (v == null) return { comp: null, num: null };
  const s = String(v).toLowerCase().trim();
  const m = s.match(/^(<=|>=|<|>)?\s*(\d+(?:\.\d+)?)/);
  if (!m) return { comp: null, num: null };
  return { comp: m[1] || null, num: parseFloat(m[2]) };
}

/**
 * AP stability (mm): <5 → 10; 5–9 → 5; >9 → 0
 */
function tkaStabilityApPoints(ap: any): number | null {
  if (ap == null) return null;
  const { comp, num } = parseCompNumber(ap);
  if (num == null) return null;

  if (comp === '<' || comp === '<=') {
    if (num <= 5) return 10;
    if (num <= 9) return 5;
    return 0;
  }
  if (comp === '>' || comp === '>=') {
    if (num > 9) return 0;
    if (num >= 5) return 5;
    return 10;
  }

  if (num < 5) return 10;
  if (num <= 9) return 5;
  return 0;
}

/**
 * Mediolateral stability (degrees), Table 17-35.c: ≤5 → 15 (most stable);
 * 6–9 → 10; 10–14 → 5; ≥15 → 0.
 * The printed "5°" row is the ≤5° (most-stable) band — a plain sub-5° value
 * (e.g. 3°) is MORE stable than 5° and must earn the 15-point band. The prior
 * strict `=== 5` awarded 15 only on exactly 5, dropping every other sub-5°
 * numeric entry to 0 points → deflated total → inflated WPI.
 */
function tkaStabilityMedLatPoints(medlat: any): number | null {
  if (medlat == null) return null;
  const { comp, num } = parseCompNumber(medlat);
  if (num == null) return null;

  const award = (x: number) =>
    (x <= 5) ? 15 :
    (x <= 9) ? 10 :
    (x <= 14) ? 5 :
    0;

  if (comp === '<' || comp === '<=') {
    if (num <= 6) return 15;     // "<6" could be 5
    if (num <= 10) return 10;    // "<10" could be 6–9
    if (num <= 15) return 5;     // "<15" could be 10–14
    return 0;
  }
  if (comp === '>' || comp === '>=') {
    if (num >= 15) return 0;
    if (num >= 10) return 5;
    if (num >= 6)  return 10;
    if (num >= 5)  return 15;
    return 15; // e.g., ">4" could include 5
  }

  return award(num);
}

function tkaFlexionContracturePoints(flex: number | null | undefined): number {
  if (flex == null) return 0;
  const v = parseFloat(String(flex));
  if (Number.isNaN(v)) return 0;
  if (v >= 5 && v <= 9) return 2;
  if (v >= 10 && v <= 15) return 5;
  if (v >= 16 && v <= 20) return 10;
  if (v > 20) return 20;
  return 0;
}

/**
 * Extension lag deduction, Table 17-35.e: <10° → 5; 10–20° → 10; >20° → 15.
 * The printed "<10°" band is the mild-lag row (1–9°); a populated 0° means NO
 * extension lag (truly-none) and must deduct 0, not fall into the <10° band and
 * subtract 5. Negatives are likewise treated as no lag.
 */
function tkaExtensionLagPoints(lag: number | null | undefined): number {
  if (lag == null) return 0;
  const v = parseFloat(String(lag));
  if (Number.isNaN(v)) return 0;
  if (v <= 0) return 0;               // no lag → no deduction
  if (v < 10) return 5;              // 1–9° (printed "<10°" band)
  if (v >= 10 && v <= 20) return 10;
  if (v > 20) return 15;
  return 0;
}

function tkaAlignmentPoints(alignment: number | null | undefined): number {
  if (alignment == null) return 0;
  const v = Math.abs(parseFloat(String(alignment)));
  if (Number.isNaN(v)) return 0;
  if (v >= 0 && v <= 4) return 0;
  if (v >= 5 && v <= 10) return v * 3;
  if (v >= 11 && v <= 15) return v * 3;
  if (v > 15) return 20;
  return 0;
}

function tkaResultAndWPI(points: number): { result: string; wpi: number } {
  if (points >= 85) return { result: "Good", wpi: 15 };
  if (points >= 50) return { result: "Fair", wpi: 20 };
  return { result: "Poor", wpi: 30 };
}

// ============= MAIN TKA CALCULATOR =============

export function calculateTKAImpairment(input: CalculatorInput): CalculatorOutput {
  const { extractedData, side = 'right' } = input;
  
  // Get TKA data from extraction
  const tka: any = side === 'left'
    ? (extractedData as any).knees?.tka_left
    : (extractedData as any).knees?.tka_right;
  
  if (!tka) {
    return {
      narrative: `No ${side} total knee arthroplasty data found in the physical examination. Unable to calculate impairment.`,
      le: 0,
      wpi: 0,
      breakdown: {
        error: 'Missing TKA data',
        side
      }
    };
  }
  
  // Calculate points
  const pain_points = tkaPainToPoints(tka.pain);
  const rom_points = tkaRangeOfMotionPoints(tka.rom_min, tka.rom_max);
  const stability_ap_points = tkaStabilityApPoints(tka.stability_ap);
  const stability_ml_points = tkaStabilityMedLatPoints(tka.stability_mediolateral);
  const flexion_contracture_points = tkaFlexionContracturePoints(tka.flexion_contracture);
  const extension_lag_points = tkaExtensionLagPoints(tka.extensor_lag);
  const alignment_points = tkaAlignmentPoints(tka.alignment);
  
  // AMA 5th ed Table 17-35: total = (pain + ROM + stability) - (flexion contracture + extensor lag + alignment)
  const total_points =
    (pain_points ?? 0) +
    (rom_points ?? 0) +
    (stability_ap_points ?? 0) +
    (stability_ml_points ?? 0) -
    (flexion_contracture_points ?? 0) -
    (extension_lag_points ?? 0) -
    (alignment_points ?? 0);
  
  const { result: tka_result, wpi: tka_wpi } = tkaResultAndWPI(total_points);
  
  // ============= BUILD NARRATIVE (EXACT from N8N) =============
  
  const capitalizedSide = side.charAt(0).toUpperCase() + side.slice(1);
  const rom_arc = (tka.rom_min != null && tka.rom_max != null) 
    ? Math.abs(tka.rom_max - tka.rom_min) 
    : null;
  
  const narrative =
    `<b><u>${capitalizedSide} TKA</u></b>\n\n` +
    `Using table 17-35 on page 549, the components of pain, range of motion and stability are added and have the sum of the flexion contracture, extension lag, and alignment subtracted. This applicant has the following breakdown of points:\n\n` +
    `-Pain: ${pain_points ?? '[not available]'} for ${tka.pain || '[not available]'} pain\n` +
    `-Range of motion: (${tka.rom_min ?? '[?]'} to ${tka.rom_max ?? '[?]'} degrees ROM) = ${rom_arc ?? '[?]'} degrees ROM / 5 degrees = ${rom_points ?? '[not available]'} points (1 point per 5 degrees, maximum 25 points)\n` +
    `-Stability (AP): ${tka.stability_ap ?? '[not available]'} mm = ${stability_ap_points ?? '[not available]'} points\n` +
    `-Stability (mediolateral): ${tka.stability_mediolateral ?? '[not available]'} degrees = ${stability_ml_points ?? '[not available]'} points\n\n` +
    `-Flexion contracture: ${flexion_contracture_points ?? '[not available]'} points for ${tka.flexion_contracture ?? '[not available]'} degree contracture\n` +
    `-Extension lag: ${extension_lag_points ?? '[not available]'} points for ${tka.extensor_lag ?? '[not available]'} degrees lag\n` +
    `-Alignment: ${alignment_points ?? '[not available]'} points for alignment\n\n` +
    `Total points = (${pain_points ?? '?'}+${rom_points ?? '?'}+${stability_ap_points ?? '?'}+${stability_ml_points ?? '?'}) - (${flexion_contracture_points ?? '?'}+${extension_lag_points ?? '?'}+${alignment_points ?? '?'})\n` +
    `= ${total_points} points, which equates to a ${tka_result ?? '[not available]'} result per Table 17-33 on page 547.\n\n` +
    `Per Table 17-33 on page 547 for a total knee replacement with a ${tka_result ? tka_result.toLowerCase() : '[not available]'} result, the applicant has a ${tka_wpi ?? '[not available]'}% WPI. This level of impairment is felt to most accurately reflect the residual effects of the work injury and takes into account the Almaraz/Guzman II decision.`;
  
  // ============= RETURN =============
  
  return {
    narrative,
    wpi: tka_wpi,
    breakdown: {
      pain_points,
      rom_points,
      stability_ap_points,
      stability_ml_points,
      flexion_contracture_points,
      extension_lag_points,
      alignment_points,
      total_points,
      result: tka_result,
    },
  };
}
